import { useRef } from 'react';
import { defaultAppTheme, defaultTheming, ThemeProvider } from '@knack/asterisk-react';
import { isAxiosError } from 'axios';

import { useLiveAppInitializationQuery } from '@/hooks/api/queries/useLiveAppInitializationQuery';
import { setApiBaseUrls, setRequestHeaders } from '@/utils/axiosConfig';
import { AppInMaintenance } from '@/components/errors/AppInMaintenance';
import { ErrorFallback } from '@/components/errors/ErrorFallback';
import { FullPageSpinner } from '@/components/FullPageSpinner';
import { AppProviders } from '@/AppProviders';
import { ThemingContextProvider } from '@/context/ThemingContext';

const isAppInitError = (error: unknown, errorCode: string): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }
  return error.response?.data?.errorCode === errorCode;
};

export function AppInitializer({ children }: { children: React.ReactNode }) {
  const isFirstLoad = useRef(true);
  const { data: initializationData, isLoading, isError, error } = useLiveAppInitializationQuery();
  const application = initializationData?.application;

  // Show loading state while initializing
  if (isLoading) {
    return (
      <ThemingContextProvider theming={defaultTheming}>
        <ThemeProvider theme={defaultAppTheme}>
          <FullPageSpinner />
        </ThemeProvider>
      </ThemingContextProvider>
    );
  }

  // Handle maintenance mode
  if (isAppInitError(error, 'app_maintenance')) {
    return (
      <ThemingContextProvider theming={defaultTheming}>
        <ThemeProvider theme={defaultAppTheme}>
          <AppProviders>
            <AppInMaintenance />
          </AppProviders>
        </ThemeProvider>
      </ThemingContextProvider>
    );
  }

  // Handle initialization error
  if (
    isAppInitError(error, 'application_not_found') ||
    isAppInitError(error, 'account_not_found') ||
    isAppInitError(error, 'domain_not_found')
  ) {
    // If we're in development, show the error instead of redirecting
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to initialize the application. In production, this will redirect to the main site.'
      );
      return (
        <div>
          Failed to initialize the application. In production, this will redirect to the main site.
        </div>
      );
    }

    // In production, redirect to the main site
    window.location.replace(import.meta.env.PUBLIC_KNACK_REDIRECT_URL);
    return null;
  }

  // Catches any other errors that do not result in a redirect
  if (isError || !application) {
    return (
      <ThemingContextProvider theming={defaultTheming}>
        <ThemeProvider theme={defaultAppTheme}>
          <AppProviders>
            <ErrorFallback
              // Shows default error message if no string is provided
              error=""
              resetErrorBoundary={() => {
                window.location.reload();
              }}
            />
          </AppProviders>
        </ThemeProvider>
      </ThemingContextProvider>
    );
  }

  if (isFirstLoad.current) {
    setRequestHeaders({ 'x-knack-application-id': application.id });
    setApiBaseUrls(
      application.settings.clientSubdomainMap.read[0],
      application.settings.clientSubdomainMap.write[0]
    );

    isFirstLoad.current = false;
  }

  return children;
}
